import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  HelpCircle,
  Loader2,
  PhoneCall,
  Search,
  Sparkles,
  User,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../shared/apiClient';
import type { Impianto, Chiamata } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { toggleMultiOpzione, isMultiOpzioneSelected } from '../shared/constants';
import { toRomeDateTimeInput, localToUtcIso } from '../shared/tz';
import {
  AvvisoDoppioniImpiantoSquadra,
  useAttivitaAperteImpiantoSquadra,
} from '../components/AvvisoDoppioniImpiantoModal';

interface AvvisoImpianto {
  id: string;
  testo: string;
  impianti: { id: string; codice: string; nome: string }[];
  isGenerale: boolean;
}

function toDatetimeLocal(d: Date): string {
  return toRomeDateTimeInput(d);
}

export function NuovaChiamataPage() {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selected, setSelected] = useState<Impianto | null>(null);
  const [dataOraChiamata, setDataOraChiamata] = useState(() => toDatetimeLocal(new Date()));
  const [segnalatoDa, setSegnalatoDa] = useState('Sala Operativa');
  const [guastoSegnalato, setGuastoSegnalato] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [confirmDestinazione, setConfirmDestinazione] = useState<'assegnata' | 'aperta' | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const attivitaImpianto = useAttivitaAperteImpiantoSquadra(selected);

  useEffect(() => {
    if (!confirmDestinazione || !selected) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [confirmDestinazione, selected]);

  // Avvisi attivi — per mostrare il banner sull'impianto selezionato
  const avvisiQuery = useQuery<AvvisoImpianto[]>({
    queryKey: ['avvisi-impianti-attivi'],
    queryFn: async () => {
      const res = await apiGet<AvvisoImpianto[]>('/avvisi-impianti', { soloAttivi: 'true' });
      return Array.isArray(res.data) ? res.data : [];
    },
    staleTime: 20000,
  });

  // Filtra avvisi che riguardano esclusivamente l'impianto selezionato (gli avvisi generali restano sul ticker)
  const avvisiImpianto = useMemo(() => {
    if (!avvisiQuery.data || !selected) return [];
    return avvisiQuery.data.filter(
      (a) => !a.isGenerale && (a.impianti || []).some((imp) => imp.id === selected.id)
    );
  }, [avvisiQuery.data, selected]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const MAX_RISULTATI_VISIBILI = 40;

  const impiantiQuery = useQuery({
    queryKey: ['impianti-search', debouncedSearch],
    enabled: !selected && debouncedSearch.trim().length >= 2,
    queryFn: async () => {
      const res = await apiGet<Impianto[]>('/impianti', { search: debouncedSearch.trim() });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const filtered = useMemo(() => {
    if (!Array.isArray(impiantiQuery.data)) return [];
    return impiantiQuery.data;
  }, [impiantiQuery.data]);

  const displayedImpianti = useMemo(() => {
    return filtered.slice(0, MAX_RISULTATI_VISIBILI);
  }, [filtered]);

  const handleSelectImpianto = (imp: Impianto) => {
    setSelected(imp);
    setSearch('');
  };

  const handleClearSelection = () => {
    setSelected(null);
    setSearch('');
  };

  const validateStep1 = (): boolean => {
    if (!selected) {
      toast({ type: 'error', message: 'Seleziona prima un impianto semaforico' });
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!dataOraChiamata) {
      toast({ type: 'error', message: 'Indica la data e l\'ora della chiamata' });
      return false;
    }
    return true;
  };

  const validateStep3 = (): boolean => {
    if (!segnalatoDa.trim()) {
      toast({ type: 'error', message: 'Indica chi ha segnalato la chiamata (es. Sala Operativa)' });
      return false;
    }
    return true;
  };

  const validateStep4 = (): boolean => {
    if (!guastoSegnalato.trim()) {
      toast({ type: 'error', message: 'Indica il guasto o l\'anomalia segnalata (es. impianto a lampeggio)' });
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (validateStep1()) setStep(2);
    } else if (step === 2) {
      if (validateStep2()) setStep(3);
    } else if (step === 3) {
      if (validateStep3()) setStep(4);
    } else if (step === 4) {
      if (validateStep4()) setStep(5);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep((step - 1) as 1 | 2 | 3 | 4 | 5);
    }
  };

  const handleGoToStep = (targetStep: 1 | 2 | 3 | 4 | 5) => {
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }
    if (targetStep >= 2 && !validateStep1()) return;
    if (targetStep >= 3 && !validateStep2()) return;
    if (targetStep >= 4 && !validateStep3()) return;
    if (targetStep >= 5 && !validateStep4()) return;
    setStep(targetStep);
  };

  const handleOpenConfirm = (destinazione: 'assegnata' | 'aperta') => {
    if (validateStep1() && validateStep2() && validateStep3() && validateStep4()) {
      setConfirmDestinazione(destinazione);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (destinazione: 'assegnata' | 'aperta') => {
      if (!selected) throw new Error('Seleziona un impianto');
      const payload = {
        impiantoId: selected.id,
        descrizione: descrizione.trim() || 'Chiamata generata dalla squadra',
        segnalatoDa: segnalatoDa.trim() || undefined,
        guastoSegnalato: guastoSegnalato.trim() || undefined,
        dataOra: localToUtcIso(dataOraChiamata),
        destinazione,
      };
      const res = await apiPost<Chiamata>('/chiamate', payload);
      return { call: res.data, destinazione };
    },
    onSuccess: ({ destinazione }) => {
      setConfirmDestinazione(null);
      if (destinazione === 'assegnata') {
        toast({ type: 'success', message: 'Chiamata creata e assegnata alla tua squadra' });
      } else {
        toast({ type: 'success', message: 'Chiamata creata e inserita tra le chiamate aperte' });
      }
      navigate('/');
    },
    onError: (err: any) => {
      toast({ type: 'error', message: err?.response?.data?.errore || 'Creazione chiamata fallita' });
    },
  });

  const stepsList = [
    { num: 1, label: 'Impianto', desc: 'Selezione' },
    { num: 2, label: 'Data & Ora', desc: 'Orario' },
    { num: 3, label: 'Segnalatore', desc: 'Ricevuta da' },
    { num: 4, label: 'Guasto', desc: 'Anomalia' },
    { num: 5, label: 'Invio', desc: 'Conferma' },
  ];

  const [suggerimentiSegnalatoDa, setSuggerimentiSegnalatoDa] = useState<string[]>([
    'Autorilevazione',
    'Sala Operativa',
    'Polizia Locale',
    'Vigili del Fuoco',
    'Cittadino',
    'Ufficio Tecnico',
    'Tecnico sul posto',
  ]);
  const [suggerimentiGuasto, setSuggerimentiGuasto] = useState<string[]>([
    'lampeggio',
    'spento',
    'lanterna urtata/girata',
    'palo a sbraccio girato',
    'centralino abbattuto',
    'non raggiungibile',
    'pulsate guasto/rotto',
    'dispositivo non vedente guasto',
  ]);

  useEffect(() => {
    apiGet<any>('/chiamate/liste-opzioni')
      .then((res) => {
        if (Array.isArray(res.data?.guastiSegnalati) && res.data.guastiSegnalati.length > 0) {
          setSuggerimentiGuasto(res.data.guastiSegnalati);
        }
        if (Array.isArray(res.data?.segnalatori) && res.data.segnalatori.length > 0) {
          const list = ['Autorilevazione', ...res.data.segnalatori.filter((s: string) => s !== 'Autorilevazione')];
          setSuggerimentiSegnalatoDa(list);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-4 animate-fade-in pb-12">
      {/* ── BARRA NAVIGAZIONE ── */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="btn-outline px-3.5 py-2 text-xs font-bold gap-1.5 shadow-2xs"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Dashboard</span>
        </button>
      </div>

      {/* ── INTESTAZIONE PAGINA ── */}
      <section className="card-section p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <PhoneCall className="w-5 h-5" />
            </div>
            <div>
              <h2 className="page-title text-base sm:text-lg">Nuova Chiamata</h2>
              <p className="text-xs text-slate-500">
                Registrazione guidata a 5 passi
              </p>
            </div>
          </div>
          <span className="badge-verde text-xs font-bold shrink-0">
            Passo {step}/5
          </span>
        </div>
      </section>

      {/* ── STEPPER NAVIGATORE A 5 PASSI ── */}
      <section className="card-section p-3 sm:p-4">
        <div className="grid grid-cols-5 gap-1 sm:gap-2">
          {stepsList.map((st) => {
            const isActive = step === st.num;
            const isCompleted = step > st.num;
            return (
              <button
                key={st.num}
                type="button"
                onClick={() => handleGoToStep(st.num as 1 | 2 | 3 | 4 | 5)}
                className={`flex flex-col items-center justify-center p-2 rounded-xl sm:rounded-2xl border transition-all text-center ${
                  isActive
                    ? 'border-emerald-600 bg-emerald-50/80 text-emerald-900 shadow-2xs font-extrabold'
                    : isCompleted
                    ? 'border-slate-200 bg-slate-50 text-emerald-700 font-bold'
                    : 'border-slate-100 bg-white text-slate-400 hover:bg-slate-50'
                }`}
              >
                <div
                  className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center font-extrabold text-[10px] sm:text-xs mb-0.5 sm:mb-1 ${
                    isActive
                      ? 'bg-emerald-600 text-white'
                      : isCompleted
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {isCompleted ? <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : st.num}
                </div>
                <span className="text-[10px] sm:text-xs font-extrabold line-clamp-1">
                  {st.label}
                </span>
                <span className="text-[9px] text-slate-400 hidden md:block">
                  {st.desc}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── CORPO WIZARD PASSO-PASSO ── */}
      <div className="space-y-4">
        {/* PASSO 1: SELEZIONE IMPIANTO */}
        {step === 1 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                  1
                </div>
                <div>
                  <h3 className="section-title mb-0">Seleziona Impianto Semaforico</h3>
                  <p className="text-xs text-slate-500">Cerca per codice o indirizzo incrocio</p>
                </div>
              </div>
              {selected && (
                <span className="badge-verde text-xs font-bold shrink-0">
                  Selezionato
                </span>
              )}
            </div>

            {selected ? (
              <div className="space-y-3">
                <div className="rounded-2xl border-2 border-emerald-500 bg-linear-to-br from-emerald-50/90 to-teal-50/50 p-4 shadow-2xs">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 uppercase tracking-wider">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Impianto Confermato
                      </div>
                      <div className="font-extrabold text-slate-900 text-base sm:text-lg">
                        {selected.codice} · {selected.nome}
                      </div>
                      {selected.tipo && (
                        <div className="text-xs text-emerald-800 font-medium">Tipologia: {selected.tipo}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-outline px-3 py-1.5 text-xs bg-white flex items-center gap-1 shrink-0 font-bold hover:bg-slate-50 border-slate-300"
                      onClick={handleClearSelection}
                    >
                      <X className="w-3.5 h-3.5" /> Cambia
                    </button>
                  </div>
                </div>

                {/* Banner + Popup anti-doppioni chiamate/segnalazioni per l'impianto selezionato */}
                <AvvisoDoppioniImpiantoSquadra
                  impianto={selected}
                  attivita={attivitaImpianto}
                  onClearImpianto={handleClearSelection}
                  contesto="chiamata"
                />

                {/* Banner avvisi per l'impianto selezionato */}
                {avvisiImpianto.length > 0 && (
                  <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 space-y-2.5 shadow-sm">
                    <div className="flex items-center gap-2 text-amber-900 font-extrabold text-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>⚠️ Avvisi di Centrale per questo impianto</span>
                    </div>
                    <div className="space-y-2">
                      {avvisiImpianto.map((a) => (
                        <div key={a.id} className="bg-white border border-amber-200 rounded-xl p-3">
                          <p className="text-xs font-semibold text-slate-800 leading-snug">{a.testo}</p>
                          {a.isGenerale && (
                            <span className="mt-1 inline-block text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                              📢 Comunicazione generale
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <Search className="w-4 h-4" />
                  </div>
                  <input
                    className="input !pl-11 !pr-11 font-semibold text-xs sm:text-sm"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Digita codice o nome (es. 01001 o Via Roma)..."
                    autoFocus
                  />
                  {search && (
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                      onClick={() => setSearch('')}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {search.trim().length < 2 ? (
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-500 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" />
                    <span>Digita almeno 2 caratteri per avviare la ricerca automatica.</span>
                  </div>
                ) : impiantiQuery.isLoading ? (
                  <div className="space-y-2">
                    <div className="skeleton h-14" />
                    <div className="skeleton h-14" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-xs sm:text-sm text-slate-600 text-center font-medium">
                    Nessun impianto trovato per "{search}".
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[320px] overflow-auto pr-1">
                    <div className="flex items-center justify-between text-xs text-slate-500 font-semibold px-1 pb-1">
                      <span>Risultati trovati ({filtered.length}):</span>
                      {filtered.length > MAX_RISULTATI_VISIBILI && (
                        <span className="text-[11px] text-amber-600 font-bold bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                          Mostrati i primi {MAX_RISULTATI_VISIBILI} (affina la ricerca)
                        </span>
                      )}
                    </div>
                    {displayedImpianti.map((imp) => (
                      <button
                        key={imp.id}
                        type="button"
                        className="w-full text-left rounded-2xl border border-slate-200 bg-white hover:border-emerald-500 hover:bg-emerald-50/40 active:bg-emerald-100/60 p-3.5 transition flex items-center justify-between gap-3 shadow-2xs group"
                        onClick={() => handleSelectImpianto(imp)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-sm text-slate-900 group-hover:text-emerald-800 break-words leading-snug">
                            {imp.codice || 'N/D'} · {imp.nome || 'Senza nome'}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {imp.tipo || 'Impianto standard'}
                          </div>
                        </div>
                        <span className="badge-outline text-[11px] font-bold text-slate-500 shrink-0 group-hover:text-emerald-700">
                          Seleziona →
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* PASSO 2: DATA E ORA CHIAMATA */}
        {step === 2 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                2
              </div>
              <div>
                <h3 className="section-title mb-0">Data e Ora Chiamata</h3>
                <p className="text-xs text-slate-500">Specifica quando è pervenuta la segnalazione</p>
              </div>
            </div>

            <div className="space-y-3 max-w-md">
              <label className="block text-xs font-bold text-slate-700">
                Data e ora ricezione chiamata <span className="text-rose-500 font-bold">*</span>
              </label>
              <div className="relative">
                <input
                  type="datetime-local"
                  className="input text-sm font-bold py-3"
                  value={dataOraChiamata}
                  onChange={(e) => setDataOraChiamata(e.target.value)}
                  required
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  className="btn-outline px-3 py-1.5 text-xs font-bold gap-1 bg-slate-50 hover:bg-slate-100"
                  onClick={() => setDataOraChiamata(toDatetimeLocal(new Date()))}
                >
                  <Clock className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Imposta a adesso</span>
                </button>
              </div>
            </div>
          </section>
        )}

        {/* PASSO 3: SEGNALAZIONE RICEVUTA DA */}
        {step === 3 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                3
              </div>
              <div>
                <h3 className="section-title mb-0">Segnalazione Ricevuta Da</h3>
                <p className="text-xs text-slate-500">Indica chi ha effettuato o inoltrato la chiamata</p>
              </div>
            </div>

            <div className="space-y-4 max-w-lg">
              {/* Opzione Chiamata Speciale: Autorilevazione (A.R.) */}
              <div className={`rounded-2xl border-2 p-4 transition ${
                segnalatoDa === 'Autorilevazione'
                  ? 'border-amber-500 bg-amber-50/80 shadow-xs'
                  : 'border-slate-200 bg-slate-50/70 hover:border-amber-300'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 text-xs font-extrabold text-amber-800 uppercase tracking-wider">
                      <Sparkles className="w-4 h-4 text-amber-600" /> Chiamata Speciale
                    </div>
                    <div className="font-extrabold text-slate-900 text-sm sm:text-base">
                      Autorilevazione Squadra (A.R.)
                    </div>
                    <p className="text-xs text-slate-600">
                      Guasto rilevato autonomamente sul posto dalla squadra durante il servizio (siglata come <strong>A.R.</strong> nel registro).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSegnalatoDa('Autorilevazione')}
                    className={`px-3 py-1.5 text-xs rounded-xl font-extrabold shrink-0 transition ${
                      segnalatoDa === 'Autorilevazione'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'bg-white border border-amber-300 text-amber-800 hover:bg-amber-100'
                    }`}
                  >
                    {segnalatoDa === 'Autorilevazione' ? '✓ Selezionata' : 'Seleziona A.R.'}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-bold text-slate-700">
                  Segnalato da <span className="text-rose-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    className="input !pl-10 text-sm font-semibold py-3"
                    value={segnalatoDa}
                    onChange={(e) => setSegnalatoDa(e.target.value)}
                    placeholder="es. Sala Operativa, Vigili Urbani, Cittadino..."
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs text-slate-500 font-semibold block">Scelta rapida segnalatore:</span>
                <div className="flex flex-wrap gap-2">
                  {suggerimentiSegnalatoDa.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => setSegnalatoDa(sug)}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition ${
                        segnalatoDa === sug
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-2xs'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {sug === 'Autorilevazione' ? '⚡ Autorilevazione (A.R.)' : sug}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* PASSO 4: GUASTO / ANOMALIA SEGNALATA */}
        {step === 4 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                4
              </div>
              <div>
                <h3 className="section-title mb-0">Guasto / Anomalia Segnalata</h3>
                <p className="text-xs text-slate-500">Descrivi il problema riscontrato all'impianto</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <label className="block text-xs font-bold text-slate-700">
                  Guasto o Anomalia segnalata <span className="text-rose-500 font-bold">*</span>
                </label>
                <div className="relative">
                  <Wrench className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    className="input !pl-10 text-sm font-semibold py-3"
                    value={guastoSegnalato}
                    onChange={(e) => setGuastoSegnalato(e.target.value)}
                    placeholder="es. impianto a lampeggio, spento..."
                    required
                  />
                </div>
                <p className="text-[11px] text-slate-400 font-medium pt-1">
                  È possibile selezionare più opzioni. Se inserisci più voci a mano, separale con un trattino " - "
                </p>
              </div>

              <div className="space-y-2">
                <span className="text-xs text-slate-500 font-semibold block">Anomalie frequenti (selezione multipla):</span>
                <div className="flex flex-wrap gap-2">
                  {suggerimentiGuasto.map((g) => {
                    const selected = isMultiOpzioneSelected(guastoSegnalato, g);
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setGuastoSegnalato(toggleMultiOpzione(guastoSegnalato, g))}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition active:scale-95 cursor-pointer ${
                          selected
                            ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-2xs'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1 pt-2">
                <label className="block text-xs font-bold text-slate-700">
                  Descrizione libera o note aggiuntive <span className="text-slate-400 font-normal">(opzionale)</span>:
                </label>
                <textarea
                  className="input min-h-[100px] resize-y text-xs font-medium"
                  value={descrizione}
                  onChange={(e) => setDescrizione(e.target.value)}
                  placeholder="Dettagli aggiuntivi (opzionale)..."
                />
              </div>
            </div>
          </section>
        )}

        {/* PASSO 5: DESTINAZIONE & INVIO */}
        {step === 5 && (
          <section className="card-section space-y-4 animate-fade-in p-5">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="w-7 h-7 rounded-full bg-slate-900 text-white font-extrabold text-xs flex items-center justify-center">
                5
              </div>
              <div>
                <h3 className="section-title mb-0">Destinazione & Inserimento</h3>
                <p className="text-xs text-slate-500">Verifica i dati e scegli la destinazione della chiamata</p>
              </div>
            </div>

            {/* RIEPILOGO COMPLETO DEI DATI INSERITI */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                <span className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                  📋 Riepilogo Nuova Chiamata
                </span>
                <span className="badge-verde text-[11px] font-bold">Verificato</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-400 font-medium block">Impianto:</span>
                  <span className="font-extrabold text-slate-900 text-sm">
                    {selected?.codice} · {selected?.nome}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Data e Ora:</span>
                  <span className="font-bold text-slate-800">
                    {dataOraChiamata
                      ? new Date(dataOraChiamata).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Ora attuale'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Segnalato da:</span>
                  <span className="font-bold text-slate-800">
                    {segnalatoDa === 'Autorilevazione' ? '⚡ Autorilevazione (A.R.)' : segnalatoDa}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Guasto segnalato:</span>
                  <span className="font-bold text-slate-800">{guastoSegnalato}</span>
                </div>
              </div>

              {descrizione && (
                <div className="border-t border-slate-200/60 pt-2 text-xs">
                  <span className="text-slate-400 font-medium block">Note aggiuntive:</span>
                  <span className="font-medium text-slate-700 italic">{descrizione}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                className="btn-primary w-full justify-center py-4 text-sm font-extrabold gap-2 shadow-md hover:shadow-lg transition"
                disabled={createMutation.isPending}
                onClick={() => handleOpenConfirm('assegnata')}
              >
                <Users className="w-4 h-4" />
                <span>Crea e Assegna a te</span>
              </button>

              <button
                type="button"
                className="btn-secondary w-full justify-center py-4 text-sm font-extrabold gap-2 hover:bg-slate-200 transition"
                disabled={createMutation.isPending}
                onClick={() => handleOpenConfirm('aperta')}
              >
                <Sparkles className="w-4 h-4" />
                <span>Crea in Coda Aperta</span>
              </button>
            </div>
          </section>
        )}
      </div>

      {/* ── BARRA INFERIORE NAVIGAZIONE WIZARD (FOOTER) ── */}
      <section className="card-section p-4 sm:p-5 shadow-sm bg-white border border-slate-200/90 rounded-2xl mt-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="btn-secondary py-3 px-4 sm:px-6 text-xs sm:text-sm font-bold gap-1.5 min-h-[46px]"
            onClick={handlePrevStep}
            disabled={step === 1 || createMutation.isPending}
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Precedente</span>
          </button>

          {step < 5 && (
            <button
              type="button"
              className="btn-primary py-3 px-5 sm:px-7 text-xs sm:text-sm font-extrabold gap-1.5 shadow-md min-h-[46px]"
              onClick={handleNextStep}
              disabled={createMutation.isPending}
            >
              <span>Successivo</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </section>

      {/* ── MODALE DI REVISIONE E CONFERMA NUOVA CHIAMATA ── */}
      {confirmDestinazione && selected && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in safe-bottom">
          <div className="w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[90dvh] sm:max-h-[85vh] overflow-hidden animate-slide-up border border-slate-200">
            {/* Header fisso */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-sm">
                  📋
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-base sm:text-lg leading-tight">Conferma Nuova Chiamata</h3>
                  <p className="text-xs text-slate-500">Verifica i dati prima dell'inserimento</p>
                </div>
              </div>
              <button
                type="button"
                className="btn-ghost p-1.5 text-slate-400 hover:text-slate-600 rounded-xl"
                onClick={() => setConfirmDestinazione(null)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corpo scrollabile */}
            <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3 text-xs sm:text-sm overscroll-contain">
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Impianto</div>
                <div className="font-extrabold text-slate-900 text-sm sm:text-base">{selected.codice} · {selected.nome}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Data e Ora</div>
                  <div className="font-bold text-slate-800">
                    {dataOraChiamata
                      ? new Date(dataOraChiamata).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'Adesso'}
                  </div>
                </div>
                <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Segnalato Da</div>
                  <div className="font-bold text-slate-800">{segnalatoDa}</div>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Guasto Segnalato</div>
                <div className="font-bold text-slate-800">{guastoSegnalato}</div>
              </div>

              {descrizione && (
                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Note aggiuntive</div>
                  <div className="font-medium text-slate-700 italic">{descrizione}</div>
                </div>
              )}

              <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="text-xs">
                  Destinazione: <strong>{confirmDestinazione === 'assegnata' ? 'Assegnata alla tua squadra' : 'Inserita in Coda Aperta'}</strong>
                </span>
              </div>
            </div>

            {/* Footer fisso */}
            <div className="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-200/90 shrink-0 grid grid-cols-2 gap-2.5 z-10">
              <button
                type="button"
                className="btn-secondary w-full justify-center text-xs sm:text-sm font-bold min-h-[44px]"
                onClick={() => setConfirmDestinazione(null)}
                disabled={createMutation.isPending}
              >
                Annulla
              </button>
              <button
                type="button"
                className="btn-primary w-full justify-center text-xs sm:text-sm font-extrabold gap-1.5 shadow-md min-h-[44px]"
                onClick={() => createMutation.mutate(confirmDestinazione)}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creazione in corso...</span>
                  </>
                ) : (
                  <>
                    <span>Conferma e Salva</span>
                    <Check className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
